import { useState, useCallback, memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { MetricEntryResponse } from '../../types/api';
import { ModalCloseButton } from '../ModalCloseButton';
import './MetricHistory.css';

interface MetricHistoryProps {
  /** All metric entries for the prospect (from prospect response) */
  allEntries: MetricEntryResponse[];
  /** The metric name to filter entries for */
  metricName: string;
  unit?: string;
  /** Current user ID to show "You" for entries recorded by the logged-in user */
  currentUserId?: string;
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "Jan 15")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) {
    return 'just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} min ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  }

  // Format as date for older entries
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface HistoryEntryProps {
  entry: MetricEntryResponse;
  unit?: string;
  currentUserId?: string;
}

const HistoryEntry = memo(function HistoryEntry({ entry, unit, currentUserId }: HistoryEntryProps) {
  const displayName = currentUserId && entry.recordedByUserId === currentUserId
    ? 'You'
    : entry.recordedByName;

  return (
    <div className="metric-history__entry">
      <span className="metric-history__value">
        {entry.value}{unit ? ` ${unit}` : ''}
      </span>
      <span className="metric-history__meta">
        {displayName} &middot; {formatRelativeTime(entry.recordedAt)}
      </span>
    </div>
  );
});

function MetricHistoryComponent({
  allEntries,
  metricName,
  unit,
  currentUserId,
}: MetricHistoryProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter all entries for this specific metric
  const allEntriesForMetric = useMemo(() => {
    return allEntries.filter(e => e.metricName === metricName);
  }, [allEntries, metricName]);

  // Show top 3 inline
  const entries = useMemo(() => {
    return allEntriesForMetric.slice(0, 3);
  }, [allEntriesForMetric]);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Don't render anything if no entries
  if (entries.length === 0) {
    return null;
  }

  // Determine if there might be more entries (if we have 3, there could be more)
  const mightHaveMore = entries.length >= 3;

  const modalContent = isModalOpen && (
    <div className="metric-history__modal-overlay" onClick={handleCloseModal}>
      <div className="metric-history__modal-card" onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton className="metric-history__close-button" onClick={handleCloseModal} />

        <h3 className="metric-history__modal-title">History</h3>
        <p className="metric-history__modal-subtitle">
          {allEntriesForMetric.length} {allEntriesForMetric.length === 1 ? 'entry' : 'entries'}
        </p>

        {allEntriesForMetric.length === 0 ? (
          <p className="metric-history__empty">No history recorded yet</p>
        ) : (
          <div className="metric-history__modal-list">
            {allEntriesForMetric.map((entry, index) => (
              <div key={entry.id}>
                <HistoryEntry entry={entry} unit={unit} currentUserId={currentUserId} />
                {index < allEntriesForMetric.length - 1 && <div className="metric-history__separator" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="metric-history">
      <div className="metric-history__recent">
        {entries.map((entry) => (
          <HistoryEntry key={entry.id} entry={entry} unit={unit} currentUserId={currentUserId} />
        ))}
      </div>

      {mightHaveMore && (
        <button
          type="button"
          className="metric-history__view-all"
          onClick={handleOpenModal}
        >
          View all history
        </button>
      )}

      {modalContent && createPortal(modalContent, document.body)}
    </div>
  );
}

export const MetricHistory = memo(MetricHistoryComponent);
